<template>
  <div class="vertical-workspace specialist-page">
    <PageHeader :icon="verticalIcon">
      <template #title>{{ workspaceTitle }}</template>
      <template #subtitle>{{ workspaceSubtitle }}</template>
    </PageHeader>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-3">{{
      errorMessage
    }}</b-alert>

    <WorkspaceTabs
      v-if="activeType && workspaceTabs.length > 1 && showWorkspaceTabs"
      :value="workspaceTabValue"
      :items="workspaceTabs"
      :aria-label="`Menu utama ${activeVerticalLabel}`"
      @change="selectWorkspaceTab"
    />

    <section
      v-if="workspaceMode === 'clinical' && selectedPatient"
      class="selected-patient-banner specialist-panel"
    >
      <span class="selected-patient-banner__avatar">{{
        patientInitials(selectedPatient.name)
      }}</span>
      <div>
        <small>PASIEN YANG SEDANG DIBUKA</small>
        <strong>{{ selectedPatient.name }}</strong>
        <span>{{ patientNumber(selectedPatient) }}</span>
      </div>
      <button type="button" @click="changePatient">
        <i class="fas fa-exchange-alt mr-1"></i>Ganti pasien
      </button>
    </section>
    <section v-if="activeVertical === 'vaccination' && vaccinationDashboard" class="vaccination-dashboard specialist-panel">
      <div class="vaccination-metric"><small>DOSIS HARI INI</small><strong>{{ vaccinationDashboard.summary.doses_today || 0 }}</strong><span>{{ vaccinationDashboard.summary.doses || 0 }} dosis periode ini</span></div>
      <div class="vaccination-metric" :class="{ attention: vaccinationDashboard.summary.overdue }"><small>JADWAL PASIEN</small><strong>{{ vaccinationDashboard.summary.due || 0 }}</strong><span>{{ vaccinationDashboard.summary.overdue || 0 }} lewat jadwal</span></div>
      <div class="vaccination-metric" :class="{ attention: vaccinationDashboard.summary.expiring_batches || vaccinationDashboard.summary.quarantined_batches }"><small>STOK VAKSIN</small><strong>{{ Number(vaccinationDashboard.summary.stock_doses || 0).toLocaleString('id-ID') }}</strong><span>{{ vaccinationDashboard.summary.expiring_batches || 0 }} batch dekat kedaluwarsa · {{ vaccinationDashboard.summary.quarantined_batches || 0 }} karantina</span></div>
      <div class="vaccination-metric"><small>PENDAPATAN</small><strong>{{ money(vaccinationDashboard.summary.revenue) }}</strong><span>Margin kotor {{ money(vaccinationDashboard.summary.gross_margin) }}</span></div>
      <button type="button" class="btn btn-light-primary" @click="$router.push({ path: '/finance', query: { service_channel: 'vaccination' } })"><i class="fas fa-chart-line mr-1"></i>Laporan keuangan</button>
    </section>
    <section v-if="workspaceMode === 'clinical' && activeVertical === 'aesthetic' && selectedPatient && aestheticPackages.length" class="patient-packages specialist-panel">
      <div><small>PAKET PASIEN</small><strong>{{ aestheticPackages.length }} paket treatment</strong></div>
      <article v-for="item in aestheticPackages" :key="item.id">
        <span>{{ item.package_snapshot.name }}</span>
        <strong>{{ item.sessions_remaining }}/{{ item.sessions_total }} sesi tersisa</strong>
        <small>{{ item.expires_at ? `Berlaku hingga ${dateOnly(item.expires_at)}` : "Tanpa batas waktu" }}</small>
      </article>
    </section>

    <section
      v-if="workspaceMode === 'clinical' && activeVertical === 'eye' && selectedPatient"
      class="eye-care-path specialist-panel"
      aria-label="Progres pelayanan Klinik Mata"
    >
      <div class="eye-care-path__intro">
        <span><i class="fas fa-eye"></i></span>
        <div>
          <small>{{ eyeEpisodeLabel }}</small>
          <strong>Satu pasien, satu rangkaian pelayanan mata</strong>
          <p>Lengkapi sesuai kebutuhan klinis; resep dibuat setelah hasil refraksi terverifikasi.</p>
        </div>
      </div>
      <div class="eye-care-steps">
        <button
          v-for="(step, index) in eyeCareSteps"
          :key="step.key"
          type="button"
          :class="{ active: activeType === step.key, complete: step.complete }"
          @click="selectType(step.key)"
        >
          <span>{{ step.complete ? '✓' : index + 1 }}</span>
          <div><strong>{{ step.label }}</strong><small>{{ step.help }}</small></div>
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      <div class="eye-care-links">
        <button type="button" @click="$router.push({ name: 'payment-list', query: { service_channel: 'eye' } })"><i class="fas fa-cash-register"></i> Kasir klinik</button>
        <button type="button" @click="$router.push({ name: 'finance-center', query: { service_channel: 'eye' } })"><i class="fas fa-chart-line"></i> Laporan Klinik Mata</button>
      </div>
    </section>

    <section
      v-if="showClinicalToolTabs"
      class="clinical-tool-switcher"
      aria-labelledby="clinical-tool-switcher-title"
    >
      <div class="clinical-tool-switcher__intro">
        <span><i class="fas fa-notes-medical"></i></span>
        <div>
          <small>AREA KERJA PASIEN</small>
          <strong id="clinical-tool-switcher-title">Dokumentasi klinis</strong>
          <p>Pilih jenis dokumentasi yang akan dikerjakan untuk pasien.</p>
        </div>
      </div>
      <WorkspaceTabs
        :value="activeType"
        :items="clinicalToolTabs"
        aria-label="Jenis dokumentasi klinis"
        @change="selectType"
      />
    </section>

    <section
      v-if="workspaceMode === 'clinical' && !selectedPatient"
      class="patient-start specialist-panel"
    >
      <div class="patient-start-intro">
        <div>
          <span class="eyebrow dark">MULAI PELAYANAN {{ activeVerticalLabel }}</span>
          <h2>Pilih pasien sekali untuk seluruh alur pelayanan</h2>
          <p>
            Setelah dipilih, pasien tetap aktif saat berpindah antarform dan
            dokumentasi klinis {{ activeVerticalLabel }}.
          </p>
        </div>
        <div class="patient-search-primary">
          <strong>Cari & pilih pasien</strong>
        <PatientPicker
          ref="patientPicker"
          :value="selectedPatient"
          label="Cari nama atau nomor rekam medis"
          @select="choosePatient"
          @clear="clearPatient"
        />
        </div>
      </div>

      <div class="specialist-workflow-guide" :aria-label="`Alur pelayanan ${activeVerticalLabel}`">
        <span><b>1</b>Pilih pasien</span>
        <i class="fas fa-chevron-right"></i>
        <span><b>2</b>{{ activeVertical === 'eye' ? 'Periksa visus' : 'Catat pemeriksaan' }}</span>
        <i class="fas fa-chevron-right"></i>
        <span><b>3</b>{{ activeVertical === 'eye' ? 'Refraksi & resep' : 'Finalisasi' }}</span>
        <i class="fas fa-chevron-right"></i>
        <span><b>4</b>{{ activeVertical === 'eye' ? 'Finalisasi & kasir' : 'Kirim ke kasir' }}</span>
      </div>

      <div class="worklist-heading">
        <div>
          <strong>Worklist {{ activeVerticalLabel }}</strong>
          <small>Pasien dengan riwayat penanganan, terbaru lebih dulu.</small>
        </div>
        <span v-if="patientWorklist.length">{{ patientWorklist.length }} pasien</span>
      </div>
      <div v-if="worklistLoading" class="worklist-loading">
        <i class="fas fa-circle-notch fa-spin"></i> Memuat pasien…
      </div>
      <div v-else-if="patientWorklist.length" class="patient-worklist">
        <button
          v-for="patient in patientWorklist"
          :key="patient.id"
          type="button"
          class="worklist-patient"
          @click="choosePatient(patient)"
        >
          <span class="worklist-avatar">{{ patientInitials(patient.name) }}</span>
          <span class="worklist-main">
            <strong>{{ patient.name }}</strong>
            <small>{{ patientNumber(patient) }}<template v-if="patient.age !== null"> · {{ patient.age }} th</template></small>
            <span class="worklist-badges">
              <b v-if="patient.records && patient.records.maternal_antenatal">ANC</b>
              <b v-if="patient.records && patient.records.maternal_growth">Tumbuh kembang</b>
              <b v-for="risk in patient.risk_flags || []" :key="risk" class="risk">{{ risk }}</b>
            </span>
          </span>
          <span class="worklist-meta">
            <small>Terakhir diperbarui</small>
            <strong>{{ dateTime(patient.last_observed_at) }}</strong>
            <small v-if="patient.next_visit_date">Kontrol: {{ patient.next_visit_date }}</small>
          </span>
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      <div v-else class="worklist-empty">
        Belum ada pasien dalam penanganan {{ activeVerticalLabel }}.
      </div>
    </section>
    <section
      v-else-if="workspaceMode === 'clinical' && activeDefinition"
      class="workspace-grid"
    >
      <aside class="record-history">
        <div class="aside-heading">
          <strong>Riwayat</strong
          ><button
            v-if="canEdit"
            class="new-record-button"
            type="button"
            title="Buat catatan baru"
            @click="newRecord"
          >
            <i class="fas fa-plus mr-1"></i> Catatan baru
          </button>
        </div>
        <button
          v-for="item in records"
          :key="item.id"
          class="history-item"
          :class="{ active: record && record.id === item.id }"
          @click="openRecord(item.id)"
        >
          <span>{{ item.record_number }}</span
          ><strong>{{ dateTime(item.observed_at) }}</strong
          ><small :class="`status-${item.status}`"
            >{{ statusLabel(item.status) }} · v{{ item.version }}</small
          >
        </button>
        <div v-if="!records.length" class="history-empty">
          Belum ada catatan.
        </div>
      </aside>

      <main class="record-card">
        <section v-if="!record" class="first-record-empty">
          <span><i class="fas fa-notes-medical"></i></span>
          <h2>Belum ada catatan {{ activeDefinition.label }}</h2>
          <p>
            Pasien ini belum memiliki riwayat pada tab ini. Buat catatan hanya
            jika pemeriksaan memang akan dilakukan.
          </p>
          <button v-if="canEdit" class="btn btn-primary" @click="newRecord">
            <i class="fas fa-plus mr-2"></i>Buat catatan pertama
          </button>
        </section>
        <div v-else class="record-heading">
          <div>
            <span>{{ verticalLabel(activeDefinition.vertical) }}</span>
            <h2>{{ activeDefinition.label }}</h2>
            <p v-if="record">
              {{ record.record_number || "Catatan baru" }} · versi
              {{ record.version || 1 }}
            </p>
          </div>
          <div v-if="record" class="heading-actions">
            <span class="status-pill" :class="`status-${record.status}`">{{
              statusLabel(record.status)
            }}</span>
            <button
              v-if="record.status === 'draft' && canEdit"
              class="btn btn-light-primary"
              @click="save"
            >
              Simpan draft
            </button>
            <button
              v-if="record.status === 'draft' && canEdit"
              class="btn btn-primary"
              @click="finalize"
            >
              Finalisasi
            </button>
            <button
              v-if="record.status === 'final' && canEdit"
              class="btn btn-light-primary"
              @click="revise"
            >
              Buat revisi
            </button>
            <button
              v-if="activeVertical === 'eye' && record.status === 'final'"
              class="btn btn-light-primary"
              @click="previewEyeInteroperability"
            >
              <i class="fas fa-exchange-alt mr-2"></i>Preview SATUSEHAT
            </button>
            <button v-if="activeType === 'mcu_conclusion' && record.status === 'final'" class="btn btn-light-primary" @click="printMcuReport">
              <i class="fas fa-print mr-2"></i>Cetak hasil MCU
            </button>
            <button
              v-if="
                activeType === 'vaccination_dose' && record.status === 'final'
              "
              class="btn btn-light-primary"
              @click="issueCertificate"
            >
              {{ certificate ? "Lihat sertifikat" : "Terbitkan sertifikat" }}
            </button>
            <button
              v-if="canSendToCashier"
              class="btn btn-success"
              @click="sendToCashier"
            >
              <i class="fas fa-cash-register mr-2"></i>Kirim ke kasir
            </button>
            <button
              v-if="record.billing"
              class="btn btn-light-success"
              @click="openInvoice"
            >
              <i class="fas fa-file-invoice mr-2"></i
              >{{ record.billing.invoice_number || "Lihat invoice" }}
            </button>
            <button
              v-if="record.id && record.status !== 'void' && canEdit"
              class="btn btn-light-danger"
              @click="voidRecord"
            >
              Batalkan
            </button>
          </div>
        </div>

        <section v-if="record && record.eye_optical_fulfillment" class="eye-optical-tracker">
          <div class="eye-optical-tracker__summary">
            <span><i class="fas fa-glasses"></i></span>
            <div>
              <small>ORDER OPTIK SETELAH PEMBAYARAN</small>
              <strong>{{ eyeFulfillmentStatusLabel(record.eye_optical_fulfillment.status) }}</strong>
              <p>Dibayar {{ dateTime(record.eye_optical_fulfillment.paid_at) }}<template v-if="record.eye_optical_fulfillment.dispensed_at"> · Diserahkan {{ dateTime(record.eye_optical_fulfillment.dispensed_at) }}</template></p>
            </div>
          </div>
          <div class="eye-optical-tracker__actions">
            <button
              v-for="action in eyeFulfillmentActions"
              :key="action.status"
              type="button"
              class="btn btn-light-primary"
              :disabled="!canEdit"
              @click="transitionEyeFulfillment(action)"
            >{{ action.label }}</button>
          </div>
          <details v-if="record.eye_optical_fulfillment.events && record.eye_optical_fulfillment.events.length">
            <summary>Riwayat proses ({{ record.eye_optical_fulfillment.events.length }})</summary>
            <div v-for="event in record.eye_optical_fulfillment.events" :key="event.id">
              <strong>{{ eyeFulfillmentStatusLabel(event.to_status) }}</strong>
              <span>{{ dateTime(event.created_at) }} · {{ event.reason }}</span>
            </div>
          </details>
        </section>

        <section v-if="certificate" class="certificate-print">
          <div class="certificate-mark">
            <i class="fas fa-shield-alt"></i><span>MEDHOP VERIFIED</span>
          </div>
          <h2>Sertifikat Vaksinasi</h2>
          <p class="certificate-number">{{ certificate.certificate_number }}</p>
          <div class="certificate-data">
            <div>
              <span>Nama pasien</span
              ><strong>{{ certificate.payload.patient_name }}</strong>
            </div>
            <div>
              <span>Vaksin</span
              ><strong>{{ certificate.payload.vaccine_name }}</strong>
            </div>
            <div>
              <span>Batch</span
              ><strong>{{ certificate.payload.batch_number }}</strong>
            </div>
            <div>
              <span>Dosis</span
              ><strong>{{ certificate.payload.dose_number }}</strong>
            </div>
            <div>
              <span>Waktu pemberian</span
              ><strong>{{
                dateTime(certificate.payload.administered_at)
              }}</strong>
            </div>
            <div>
              <span>Dosis berikutnya</span
              ><strong>{{ certificate.payload.next_dose_date || "-" }}</strong>
            </div>
          </div>
          <p class="verification-code">
            Verifikasi publik:
            <a :href="verificationUrl" target="_blank" rel="noopener">{{
              verificationUrl
            }}</a>
          </p>
          <div class="certificate-actions">
            <button class="btn btn-light-primary" @click="windowPrint">
              Cetak</button
            ><button class="btn btn-light-primary" @click="copyVerificationUrl">
              Salin tautan</button
            ><button
              v-if="!certificate.revoked_at && canEdit"
              class="btn btn-light-danger"
              @click="revokeCertificate"
            >
              Cabut sertifikat
            </button>
          </div>
        </section>

        <section v-if="trendSeries.length > 1" class="progress-chart">
          <div>
            <span>{{ trendTitle }}</span
            ><strong>{{ trendSeries.length }} pengukuran</strong>
          </div>
          <svg viewBox="0 0 600 180" role="img" :aria-label="trendTitle">
            <line x1="36" y1="150" x2="580" y2="150" />
            <line x1="36" y1="20" x2="36" y2="150" />
            <polyline :points="trendPoints" />
            <circle
              v-for="point in plottedTrend"
              :key="point.key"
              :cx="point.x"
              :cy="point.y"
              r="5"
            >
              <title>{{ point.label }}</title>
            </circle>
          </svg>
          <div class="trend-legend">
            <span v-for="point in trendSeries" :key="point.key"
              ><b>{{ point.axis }}</b
              >{{ point.value }}</span
            >
          </div>
        </section>

        <div v-if="record" class="clinical-form">
          <div class="form-field">
            <label>Waktu observasi</label
            ><input
              v-model="record.observed_at"
              :disabled="!editable"
              type="datetime-local"
              class="form-control"
            />
          </div>
          <nav v-if="activeType === 'mcu_conclusion'" class="mcu-stepper wide" aria-label="Tahapan Medical Check Up">
            <button v-for="(section, index) in mcuSections" :key="section.key" type="button" :class="{ active: mcuSection === section.key }" @click="mcuSection = section.key">
              <span>{{ index + 1 }}</span><strong>{{ section.label }}</strong>
            </button>
          </nav>
          <section v-if="activeType === 'mcu_conclusion' && mcuSection === 'order'" class="mcu-order-context wide">
            <div class="form-field">
              <label>Pendaftaran / Kunjungan</label>
              <select v-model="record.appointment_id" :disabled="!editable" class="form-control" @change="applyMcuAppointment">
                <option :value="null">Tanpa pendaftaran</option>
                <option v-for="item in mcuContext.appointments || []" :key="item.id" :value="item.id">{{ dateOnly(item.date) }} · {{ item.doctor_name || 'Dokter belum ditentukan' }}</option>
              </select>
            </div>
            <div class="form-field">
              <label>Dokter Pemeriksa <b>*</b></label>
              <select v-model="record.doctor_id" :disabled="!editable" class="form-control">
                <option :value="null">Pilih dokter</option>
                <option v-for="doctor in mcuContext.doctors || []" :key="doctor.id" :value="doctor.id">{{ doctor.name }}</option>
              </select>
            </div>
            <div class="form-field wide">
              <label>Peserta Program Corporate MCU</label>
              <select v-model="record.corporate_mcu_participant_id" :disabled="!editable" class="form-control" @change="applyMcuParticipant">
                <option :value="null">Bukan peserta program corporate</option>
                <option v-for="item in mcuContext.corporate_participants || []" :key="item.id" :value="item.id">{{ item.company_name }} · {{ item.program_name }} · {{ item.employee_number }}</option>
              </select>
            </div>
          </section>
          <McuResultAttachments v-if="activeType === 'mcu_conclusion' && mcuSection === 'supporting'" :record-id="record.id" :editable="editable" />
          <AestheticBeforeAfterMedia
            v-if="activeType === 'aesthetic_before_after'"
            :record-id="record.id"
            :editable="editable"
            :standardized-view="record.clinical_payload.standardized_view || ''"
            @ensure-record="ensureAestheticRecordForMedia"
            @view-change="setAestheticStandardizedView"
          />
          <div
            v-for="field in visibleClinicalFields"
            :key="field.key"
            class="form-field"
            :class="[
              { wide: ['textarea', 'array', 'resource_quantity'].includes(field.type) },
              eyeFieldClass(field)
            ]"
          >
            <label>{{ field.label }} <b v-if="field.required">*</b></label>
            <select
              v-if="field.type === 'select'"
              v-model="record.clinical_payload[field.key]"
              :disabled="!editable"
              class="form-control"
              @change="applyClinicalDefaults(field.key)"
            >
              <option value="">Pilih</option>
              <option
                v-for="(label, value) in field.options"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
            <select
              v-else-if="field.type === 'resource'"
              v-model="record.clinical_payload[field.key]"
              :disabled="!editable"
              class="form-control"
              @change="applyResourceDefaults(field)"
            >
              <option value="">{{ field.required ? `Pilih ${field.label.toLowerCase()}` : "Tanpa paket / layanan satuan" }}</option>
              <option
                v-for="option in resourceOptions[field.resource_type] || []"
                :key="option.id"
                :value="option.id"
              >
                {{ resourceOptionLabel(option, field.resource_type) }}
              </option>
            </select>
            <div v-else-if="field.type === 'resource_quantity'" class="clinical-resource-lines">
              <div v-for="(line, index) in record.clinical_payload[field.key]" :key="`${field.key}-${index}`" class="clinical-resource-line">
                <select v-model="line.resource_id" :disabled="!editable" class="form-control">
                  <option value="">Pilih batch produk</option>
                  <option v-for="option in resourceOptions[field.resource_type] || []" :key="option.id" :value="option.id">{{ resourceOptionLabel(option, field.resource_type) }}</option>
                </select>
                <input v-model.number="line.quantity" :disabled="!editable" type="number" min="0.0001" step="any" class="form-control" aria-label="Jumlah pemakaian" />
                <input v-model="line.unit" :disabled="!editable" class="form-control" placeholder="satuan" />
                <button v-if="editable" type="button" class="btn btn-light-danger" @click="removeClinicalResourceLine(field.key, index)" aria-label="Hapus batch"><i class="fas fa-trash"></i></button>
              </div>
              <button v-if="editable" type="button" class="btn btn-light-primary" @click="addClinicalResourceLine(field.key)"><i class="fas fa-plus mr-1"></i>Tambah batch</button>
              <small>Batch disarankan otomatis menggunakan FEFO dan stok dikurangi saat catatan difinalisasi.</small>
            </div>
            <label v-else-if="field.type === 'boolean'" class="boolean-input"
              ><input
                v-model="record.clinical_payload[field.key]"
                :disabled="!editable"
                type="checkbox"
              /><span>{{ booleanLabel(field.key) }}</span></label
            >
            <textarea
              v-else-if="field.type === 'textarea'"
              v-model="record.clinical_payload[field.key]"
              :disabled="!editable"
              rows="3"
              class="form-control"
            ></textarea>
            <textarea
              v-else-if="field.type === 'array'"
              :value="arrayValue(field.key)"
              :disabled="!editable"
              rows="3"
              class="form-control"
              placeholder="Satu item per baris"
              @input="setArrayValue(field.key, $event.target.value)"
            ></textarea>
            <input
              v-else
              v-model="record.clinical_payload[field.key]"
              :disabled="!editable"
              :type="inputType(field.type)"
              :min="field.min"
              :max="field.max_value"
              :step="field.type === 'number' ? 'any' : null"
              class="form-control"
              @change="applyClinicalDefaults(field.key)"
            />
          </div>
          <div class="form-field wide">
            <label>Ringkasan klinis</label
            ><textarea
              v-model="record.clinical_summary"
              :disabled="!editable"
              rows="4"
              class="form-control"
            ></textarea>
          </div>
        </div>

        <details
          v-if="record && record.events && record.events.length"
          class="audit-trail"
        >
          <summary>Jejak audit ({{ record.events.length }})</summary>
          <div v-for="event in record.events" :key="event.id">
            <strong>{{ event.event_type }}</strong
            ><span
              >{{ event.actor_name }} · {{ dateTime(event.created_at) }}</span
            ><small>{{ event.reason }}</small>
          </div>
        </details>
      </main>
    </section>
    <VerticalResourceWorkspace
      v-else-if="workspaceMode === 'resources'"
      :vertical-filter="activeVertical"
      :resource-type="activeResourceType"
      :show-tabs="false"
      embedded
    />
    <b-modal id="eye-fhir-preview-modal" size="xl" title="Preview Interoperabilitas SATUSEHAT" hide-footer>
      <b-alert v-if="eyeFhirPreview && !eyeFhirPreview.integration_ready" show variant="warning">
        Payload belum siap dikirim. Lengkapi IHS Number pasien, Encounter SATUSEHAT, dan verifikasi terminologi.
      </b-alert>
      <ul v-if="eyeFhirPreview && eyeFhirPreview.warnings" class="eye-fhir-warnings">
        <li v-for="warning in eyeFhirPreview.warnings" :key="warning">{{ warning }}</li>
      </ul>
      <pre class="eye-fhir-preview">{{ eyeFhirPreview ? JSON.stringify(eyeFhirPreview.bundle, null, 2) : '' }}</pre>
    </b-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import VerticalResourceWorkspace from "@/view/pages/verticals/VerticalResourceWorkspace.vue";
import WorkspaceTabs from "@/component/general/WorkspaceTabs.vue";
import PatientPicker from "@/component/general/PatientPicker.vue";
import AestheticBeforeAfterMedia from "@/component/aesthetic/AestheticBeforeAfterMedia.vue";
import McuResultAttachments from "@/component/mcu/McuResultAttachments.vue";

export default {
  name: "VerticalClinicalWorkspace",
  components: { VerticalResourceWorkspace, WorkspaceTabs, PatientPicker, AestheticBeforeAfterMedia, McuResultAttachments },
  data() {
    return {
      registry: {},
      resourceRegistry: {},
      activeType: null,
      activeResourceType: null,
      workspaceMode:
        this.$route.query.tab === "resources" ? "resources" : "clinical",
      selectedPatient: null,
      records: [],
      record: null,
      certificate: null,
      vaccinationDashboard: null,
      resourceOptions: {},
      aestheticPackages: [],
      patientWorklist: [],
      mcuContext: { appointments: [], doctors: [], corporate_participants: [] },
      mcuSection: "order",
      worklistLoading: false,
      errorMessage: "",
      eyeFhirPreview: null,
    };
  },
  computed: {
    activeDefinition() {
      return this.registry[this.activeType] || null;
    },
    visibleClinicalFields() {
      const fields = (this.activeDefinition && this.activeDefinition.fields) || [];
      if (this.activeType === "mcu_conclusion")
        return fields.filter((field) => (field.section || "order") === this.mcuSection);
      if (this.activeType === "vaccination_dose") {
        const derived = ["vaccine_name", "manufacturer", "kfa_code", "cvx_code", "batch_number", "expiry_date", "cold_chain_verified"];
        return fields.filter((field) => !derived.includes(field.key));
      }
      return this.activeType === "aesthetic_before_after"
        ? fields.filter((field) => field.key !== "standardized_view")
        : fields;
    },
    activeVertical() {
      return this.activeDefinition ? this.activeDefinition.vertical : null;
    },
    activeVerticalLabel() {
      return this.verticalLabel(this.activeVertical) || "layanan spesialis";
    },
    showWorkspaceTabs() {
      return true;
    },
    definitions() {
      return Object.keys(this.registry)
        .map((code) => ({ code, ...this.registry[code] }))
        .filter(
          (item) =>
            !this.activeVertical || item.vertical === this.activeVertical
        );
    },
    operationalDefinitions() {
      return Object.keys(this.resourceRegistry)
        .map((code) => ({ code, ...this.resourceRegistry[code] }))
        .filter((item) => item.vertical === this.activeVertical)
        .sort((left, right) => {
          const order = this.activeVertical === "aesthetic"
            ? [
                "aesthetic_treatment_package",
                "aesthetic_treatment",
                "aesthetic_product",
                "aesthetic_product_batch",
              ]
            : this.activeVertical === "eye"
            ? ["eye_service_package", "eye_diagnostic_test", "eye_frame", "eye_lens"]
            : this.activeVertical === "vaccination"
            ? ["vaccination_vaccine_product", "vaccination_vaccine_batch", "vaccination_cold_chain_log"]
            : [];
          if (!order.length) return 0;
          return order.indexOf(left.code) - order.indexOf(right.code);
        });
    },
    clinicalToolTabs() {
      return this.definitions.map((item) => ({
        key: item.code,
        label: item.label,
        icon:
          item.code === "aesthetic_before_after"
            ? "fas fa-images"
            : item.code === "aesthetic_body_map"
            ? "fas fa-street-view"
            : item.code === "maternal_antenatal"
            ? "fas fa-user-md"
            : item.code === "maternal_growth"
            ? "fas fa-child"
            : item.code === "eye_visual_acuity"
            ? "fas fa-low-vision"
            : item.code === "eye_refraction"
            ? "fas fa-glasses"
            : item.code === "eye_eyeglass_prescription"
            ? "fas fa-file-prescription"
            : item.code === "eye_comprehensive_exam"
            ? "fas fa-user-md"
            : item.code === "eye_diagnostic_result"
            ? "fas fa-microscope"
            : item.code === "eye_optical_order"
            ? "fas fa-shopping-bag"
            : this.verticalIcon,
      }));
    },
    showClinicalToolTabs() {
      return (
        ["aesthetic", "maternal_child", "eye"].includes(this.activeVertical) &&
        this.workspaceMode === "clinical" &&
        this.clinicalToolTabs.length > 1
      );
    },
    workspaceTabs() {
      if (this.activeVertical === "aesthetic")
        return [
          {
            key: "worklist",
            label: "Worklist",
            icon: "fas fa-clipboard-list",
          },
          ...this.operationalDefinitions.map((item) => ({
            key: `resource:${item.code}`,
            label: item.label,
            icon: this.resourceIcon(item.code),
          })),
        ];
      if (this.activeVertical === "maternal_child")
        return [
          {
            key: "worklist",
            label: "Worklist",
            icon: "fas fa-clipboard-list",
          },
        ];
      if (this.activeVertical === "eye")
        return [
          {
            key: "worklist",
            label: "Worklist Pasien",
            icon: "fas fa-clipboard-list",
          },
          ...this.operationalDefinitions.map((item) => ({
            key: `resource:${item.code}`,
            label: item.label,
            icon: this.resourceIcon(item.code),
          })),
        ];
      return [
        ...this.definitions.map((item) => ({
          key: item.code,
          label: item.label,
          icon: this.verticalIcon,
        })),
        ...this.operationalDefinitions.map((item) => ({
          key: `resource:${item.code}`,
          label: item.label,
          icon: this.resourceIcon(item.code),
        })),
      ];
    },
    workspaceTabValue() {
      if (
        ["aesthetic", "maternal_child", "eye"].includes(this.activeVertical) &&
        this.workspaceMode === "clinical"
      )
        return "worklist";
      return this.workspaceMode === "resources" && this.activeResourceType
        ? `resource:${this.activeResourceType}`
        : this.activeType;
    },
    hasOperationalResources() {
      return Object.values(this.resourceRegistry).some(
        (item) => item.vertical === this.activeVertical
      );
    },
    workspaceTitle() {
      if (this.activeVertical === "aesthetic") return "Klinik Kecantikan";
      if (this.activeVertical === "maternal_child") return "Klinik Ibu & Anak";
      if (this.activeVertical === "eye") return "Klinik Mata";
      if (this.activeVertical === "mcu") return "Medical Check Up";
      return this.workspaceMode === "resources"
        ? `${this.activeVerticalLabel} · Katalog & Operasional`
        : this.activeDefinition
        ? this.activeDefinition.label
        : this.activeVerticalLabel;
    },
    workspaceSubtitle() {
      if (this.activeVertical === "aesthetic") {
        if (this.workspaceMode === "resources") {
          const definition = this.resourceRegistry[this.activeResourceType];
          return definition
            ? `Kelola ${definition.label.toLowerCase()} sebagai sumber data operasional klinik.`
            : "Kelola master dan paket treatment Klinik Kecantikan.";
        }
        return "Kelola worklist pasien dan dokumentasi klinis dalam satu alur kerja.";
      }
      if (this.activeVertical === "maternal_child")
        return "Kelola worklist pasien, dokumentasi klinis, penagihan, dan laporan KIA dalam satu alur kerja.";
      if (this.activeVertical === "eye")
        return "Kelola pemeriksaan visus, refraksi, resep kacamata, penagihan, dan laporan dalam satu alur pasien.";
      if (this.activeVertical === "mcu")
        return "Kelola registrasi, paket, pemeriksaan lengkap, hasil penunjang, kesimpulan, corporate MCU, dan kasir dalam satu episode.";
      return this.workspaceMode === "resources"
        ? `Kelola katalog ${this.activeVerticalLabel} untuk seluruh pasien dan transaksi cabang.`
        : `Dokumentasi klinis ${this.activeVerticalLabel} berdasarkan satu konteks pasien.`;
    },
    verticalIcon() {
      return (
        {
          aesthetic: "fas fa-spa",
          maternal_child: "fas fa-baby",
          mcu: "fas fa-clipboard-check",
          eye: "fas fa-eye",
          physiotherapy: "fas fa-walking",
          occupational: "fas fa-hard-hat",
          vaccination: "fas fa-syringe",
        }[this.activeVertical] || "fas fa-clinic-medical"
      );
    },
    canEdit() {
      let rights = [];
      try {
        rights = JSON.parse(localStorage.getItem("access_right_display")) || [];
      } catch (e) {}
      return rights.length === 0 || rights.map(String).includes("1202");
    },
    editable() {
      return this.record && this.record.status === "draft" && this.canEdit;
    },
    canSendToCashier() {
      return (
        [
          "aesthetic_before_after",
          "maternal_antenatal",
          "maternal_growth",
          "eye_visual_acuity",
          "eye_refraction",
          "eye_comprehensive_exam",
          "eye_diagnostic_result",
          "eye_optical_order",
          "vaccination_dose",
          "mcu_conclusion",
        ].includes(this.activeType) &&
        this.record &&
        this.record.id &&
        (this.record.status === "final" || (this.activeType === "mcu_conclusion" && this.record.status === "draft")) &&
        !this.record.billing
      );
    },
    mcuSections() {
      return [
        { key: "order", label: "Registrasi & Paket" },
        { key: "vitals", label: "Tanda Vital" },
        { key: "history", label: "Riwayat" },
        { key: "physical", label: "Pemeriksaan Fisik" },
        { key: "supporting", label: "Hasil Penunjang" },
        { key: "conclusion", label: "Kesimpulan" },
      ];
    },
    verificationUrl() {
      return this.certificate
        ? `${window.location.origin}${
            window.location.pathname
          }#/verify-vaccination/${encodeURIComponent(
            this.certificate.verification_token
          )}`
        : "";
    },
    trendSeries() {
      const rows = this.records.map((row) => ({
        row,
        payload: this.payloadOf(row),
      }));
      if (this.activeType === "maternal_growth")
        return rows
          .filter(
            (x) =>
              x.payload.age_months !== undefined &&
              x.payload.weight_kg !== undefined
          )
          .map((x) => ({
            key: x.row.id,
            axis: `${x.payload.age_months} bln`,
            value: Number(x.payload.weight_kg),
            label: `Usia ${x.payload.age_months} bulan: ${x.payload.weight_kg} kg`,
          }))
          .sort(
            (a, b) =>
              Number(a.axis.split(" ")[0]) - Number(b.axis.split(" ")[0])
          );
      if (this.activeType === "physio_progress")
        return rows
          .filter(
            (x) =>
              x.payload.session_number !== undefined &&
              x.payload.pain_score !== undefined
          )
          .map((x) => ({
            key: x.row.id,
            axis: `Sesi ${x.payload.session_number}`,
            value: Number(x.payload.pain_score),
            label: `Sesi ${x.payload.session_number}: nyeri ${x.payload.pain_score}`,
          }))
          .sort(
            (a, b) =>
              Number(a.axis.replace(/\D/g, "")) -
              Number(b.axis.replace(/\D/g, ""))
          );
      return [];
    },
    plottedTrend() {
      if (this.trendSeries.length < 2) return [];
      const values = this.trendSeries.map((x) => x.value),
        min = Math.min(...values),
        max = Math.max(...values),
        range = max - min || 1;
      return this.trendSeries.map((item, index) => ({
        ...item,
        x: 36 + (544 * index) / (this.trendSeries.length - 1),
        y: 145 - (120 * (item.value - min)) / range,
      }));
    },
    trendPoints() {
      return this.plottedTrend
        .map((point) => `${point.x},${point.y}`)
        .join(" ");
    },
    trendTitle() {
      return this.activeType === "maternal_growth"
        ? "Tren berat badan menurut usia"
        : "Tren skala nyeri per sesi";
    },
    eyeCareSteps() {
      if (this.activeVertical !== "eye") return [];
      const patientRecords = (this.selectedPatient && this.selectedPatient.records) || {};
      return [
        { key: "eye_visual_acuity", label: "Visus", help: "Tajam penglihatan & TIO", complete: Boolean(patientRecords.eye_visual_acuity) },
        { key: "eye_comprehensive_exam", label: "Pemeriksaan lengkap", help: "Slit lamp, fundus, diagnosis", complete: Boolean(patientRecords.eye_comprehensive_exam) },
        { key: "eye_diagnostic_result", label: "Diagnostik", help: "OCT, fundus, perimetry", complete: Boolean(patientRecords.eye_diagnostic_result) },
        { key: "eye_refraction", label: "Refraksi", help: "OD/OS, BCVA, PD", complete: Boolean(patientRecords.eye_refraction) },
        { key: "eye_eyeglass_prescription", label: "Resep", help: "Lensa & instruksi", complete: Boolean(patientRecords.eye_eyeglass_prescription) },
        { key: "eye_optical_order", label: "Order optik", help: "Frame, lensa, lab & QC", complete: Boolean(patientRecords.eye_optical_order) },
      ];
    },
    eyeEpisodeLabel() {
      const episode = this.record && this.record.eye_episode;
      return episode && episode.episode_number ? `EPISODE ${episode.episode_number} · ${String(episode.status || "open").toUpperCase()}` : "ALUR PEMERIKSAAN PASIEN";
    },
    eyeFulfillmentActions() {
      const fulfillment = this.record && this.record.eye_optical_fulfillment;
      if (!fulfillment) return [];
      const actions = {
        processing: [{ status: "sent_to_lab", label: "Kirim ke lab" }, { status: "ready", label: "Tandai siap" }, { status: "remake", label: "Buat ulang" }],
        sent_to_lab: [{ status: "quality_check", label: "Mulai QC" }, { status: "remake", label: "Buat ulang" }],
        quality_check: [{ status: "ready", label: "Lulus QC & siap" }, { status: "remake", label: "Buat ulang" }],
        ready: [{ status: "dispensed", label: "Serahkan ke pasien" }, { status: "remake", label: "Buat ulang" }],
        remake: [{ status: "sent_to_lab", label: "Kirim ulang ke lab" }, { status: "quality_check", label: "Mulai QC" }, { status: "ready", label: "Tandai siap" }],
      };
      return actions[fulfillment.status] || [];
    },
  },
  async mounted() {
    await this.loadRegistry();
    this.updateBreadcrumb();
  },
  watch: {
    "$route.params.recordType"(value) {
      if (value && this.registry[value]) this.selectType(value);
    },
    "$route.query.tab"(tab) {
      this.workspaceMode =
        tab === "resources" && this.hasOperationalResources
          ? "resources"
          : "clinical";
    },
    "$route.query.resource"(type) {
      if (
        type &&
        this.resourceRegistry[type] &&
        this.resourceRegistry[type].vertical === this.activeVertical
      )
        this.activeResourceType = type;
    },
  },
  methods: {
    eyeFulfillmentStatusLabel(status) {
      return ({ processing: "Sedang diproses", sent_to_lab: "Dikirim ke lab", quality_check: "Pemeriksaan kualitas", ready: "Siap diambil", dispensed: "Sudah diserahkan", remake: "Pembuatan ulang", void: "Dibatalkan" })[status] || status;
    },
    async transitionEyeFulfillment(action) {
      const reason = window.prompt(`Catatan untuk status “${action.label}”:`, action.label);
      if (!reason) return;
      try {
        const fulfillment = this.record.eye_optical_fulfillment;
        const response = await ApiService.post(`vertical-clinical/eye-optical-fulfillments/${fulfillment.id}/transition`, { status: action.status, reason });
        this.record.eye_optical_fulfillment = response.data.data;
        this.toast(`Order optik: ${this.eyeFulfillmentStatusLabel(action.status)}.`, "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async previewEyeInteroperability() {
      try {
        const response = await ApiService.get(`vertical-clinical/records/${this.record.id}/eye-interoperability-preview`);
        this.eyeFhirPreview = response.data.data;
        this.$bvModal.show("eye-fhir-preview-modal");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    eyeFieldClass(field) {
      if (this.activeVertical !== "eye" || !field || !field.key) return "";
      if (field.key.indexOf("od_") === 0) return "eye-field eye-field--od";
      if (field.key.indexOf("os_") === 0) return "eye-field eye-field--os";
      return "";
    },
    async loadRegistry() {
      try {
        const [clinical, resources] = await Promise.all([
          ApiService.get("vertical-clinical/registry"),
          ApiService.get("vertical-resources/registry").catch(() => ({
            data: { data: {} },
          })),
        ]);
        this.registry = clinical.data.data || {};
        this.resourceRegistry = resources.data.data || {};
        const requested = this.$route.params.recordType;
        this.activeType =
          (requested && this.registry[requested]
            ? requested
            : Object.keys(this.registry)[0]) || null;
        const requestedResource = this.$route.query.resource;
        this.activeResourceType =
          (requestedResource &&
          this.resourceRegistry[requestedResource] &&
          this.resourceRegistry[requestedResource].vertical ===
            this.activeVertical
            ? requestedResource
            : this.operationalDefinitions[0] &&
              this.operationalDefinitions[0].code) || null;
        await this.loadResourceOptions();
        await this.loadPatientWorklist();
        await this.loadVaccinationDashboard();
        await this.openPatientFromQuery();
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    selectType(code) {
      const previousVertical = this.activeVertical;
      this.activeType = code;
      this.record = null;
      this.certificate = null;
      this.records = [];
      this.updateBreadcrumb();
      this.loadResourceOptions();
      if (previousVertical !== this.activeVertical) {
        this.loadPatientWorklist();
        this.loadVaccinationDashboard();
      }
      if (this.$route.params.recordType !== code)
        this.$router.replace(`/vertical-clinical/${code}`);
      if (this.selectedPatient) this.loadRecords();
    },
    selectWorkspaceTab(value) {
      if (value === "worklist") {
        this.selectWorkspace("clinical");
        return;
      }
      if (value.startsWith("resource:")) {
        this.activeResourceType = value.slice(9);
        this.selectWorkspace("resources");
        return;
      }
      this.selectWorkspace("clinical");
      this.selectType(value);
    },
    selectWorkspace(mode) {
      this.workspaceMode = mode;
      const query = {
        ...this.$route.query,
      };
      if (mode === "resources" && this.activeResourceType) {
        query.tab = "resources";
        query.resource = this.activeResourceType;
      } else {
        delete query.tab;
        delete query.resource;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    updateBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: "Layanan Spesialis" },
        { title: this.activeVerticalLabel },
      ]);
    },
    async loadResourceOptions() {
      const fields =
        (this.activeDefinition && this.activeDefinition.fields) || [];
      const types = [
        ...new Set(
          fields
            .filter((field) => ["resource", "resource_quantity"].includes(field.type))
            .map((field) => field.resource_type)
        ),
      ];
      await Promise.all(
        types.map(async (type) => {
          try {
            const r = await ApiService.query("vertical-resources", {
              params: {
                resource_type: type,
                status: "active",
                available_now: 1,
                page_size: 100,
              },
            });
            this.$set(
              this.resourceOptions,
              type,
              this.items(r.data).map((item) => ({
                ...item,
                operational_payload: this.payloadOf({
                  clinical_payload: item.operational_payload,
                }),
              }))
            );
          } catch (e) {
            this.$set(this.resourceOptions, type, []);
          }
        })
      );
    },
    items(payload) {
      if (Array.isArray(payload.data)) return payload.data;
      if (payload.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (Array.isArray(payload.items)) return payload.items;
      return [];
    },
    choosePatient(patient) {
      if (this.activeVertical === "maternal_child" && patient.records) {
        const hasAnc = Boolean(patient.records.maternal_antenatal);
        const hasGrowth = Boolean(patient.records.maternal_growth);
        if (hasGrowth && !hasAnc && this.activeType !== "maternal_growth")
          this.selectType("maternal_growth");
        else if (hasAnc && !hasGrowth && this.activeType !== "maternal_antenatal")
          this.selectType("maternal_antenatal");
      }
      this.selectedPatient = patient;
      this.errorMessage = "";
      if (this.activeVertical === "aesthetic") this.loadAestheticPackages();
      if (this.activeVertical === "mcu") this.loadMcuContext();
      this.loadRecords();
    },
    clearPatient() {
      this.selectedPatient = null;
      this.records = [];
      this.record = null;
      this.certificate = null;
      this.aestheticPackages = [];
      this.mcuContext = { appointments: [], doctors: [], corporate_participants: [] };
      this.errorMessage = "";
    },
    changePatient() {
      this.clearPatient();
    },
    async loadAestheticPackages() {
      try {
        const response = await ApiService.get(`vertical-clinical/aesthetic/patients/${this.selectedPatient.id}/packages`);
        this.aestheticPackages = response.data.data || [];
      } catch (e) {
        this.aestheticPackages = [];
      }
    },
    async loadMcuContext() {
      if (!this.selectedPatient || this.activeVertical !== "mcu") return;
      try {
        const response = await ApiService.get(`vertical-clinical/mcu/patients/${this.selectedPatient.id}/context`);
        this.mcuContext = response.data.data || { appointments: [], doctors: [], corporate_participants: [] };
      } catch (e) {
        this.mcuContext = { appointments: [], doctors: [], corporate_participants: [] };
        this.errorMessage = this.error(e);
      }
    },
    async openPatientFromQuery() {
      const patientId = Number(this.$route.query.patient_id || 0);
      if (!patientId) return;
      try {
        if (this.activeVertical === "mcu") {
          const response = await ApiService.get(`vertical-clinical/mcu/patients/${patientId}/context`);
          const context = response.data.data;
          this.mcuContext = context;
          this.choosePatient(context.patient);
        } else {
          const response = await ApiService.get(`patients/${patientId}`);
          const patient = response.data && response.data.data ? response.data.data : response.data;
          if (patient && patient.id) this.choosePatient(patient);
        }
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    applyMcuAppointment() {
      const appointment = (this.mcuContext.appointments || []).find((item) => Number(item.id) === Number(this.record.appointment_id));
      if (appointment && appointment.doctor_id) this.record.doctor_id = appointment.doctor_id;
    },
    applyMcuParticipant() {
      const participant = (this.mcuContext.corporate_participants || []).find((item) => Number(item.id) === Number(this.record.corporate_mcu_participant_id));
      if (!participant) {
        this.record.corporate_mcu_program_id = null;
        return;
      }
      this.record.corporate_mcu_program_id = participant.corporate_mcu_program_id;
      this.$set(this.record.clinical_payload, "payer_type", "corporate");
      this.$set(this.record.clinical_payload, "company_name", participant.company_name || "");
      this.$set(this.record.clinical_payload, "employee_number", participant.employee_number || "");
      this.$set(this.record.clinical_payload, "work_area", participant.department || "");
    },
    async loadPatientWorklist() {
      if (!this.activeVertical) return;
      this.worklistLoading = true;
      try {
        const response = await ApiService.query(
          `vertical-clinical/patient-worklist/${this.activeVertical}`,
          { params: { limit: 50 } }
        );
        this.patientWorklist = response.data.data || [];
      } catch (e) {
        this.patientWorklist = [];
        this.errorMessage = this.error(e);
      } finally {
        this.worklistLoading = false;
      }
    },
    patientInitials(name) {
      return String(name || "P")
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase();
    },
    async loadRecords() {
      if (!this.selectedPatient || !this.activeType) return;
      try {
        const r = await ApiService.query("vertical-clinical/records", {
          params: {
            patient_id: this.selectedPatient.id,
            record_type: this.activeType,
            page_size: 100,
          },
        });
        this.records = this.items(r.data);
        const requestedId = Number(this.$route.query.record_id || 0);
        const requested = requestedId && this.records.find(item => Number(item.id) === requestedId);
        if (this.records.length) await this.openRecord(requested ? requested.id : this.records[0].id);
        else {
          this.record = null;
          this.certificate = null;
        }
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async loadVaccinationDashboard() {
      if (this.activeVertical !== "vaccination") {
        this.vaccinationDashboard = null;
        return;
      }
      try {
        const response = await ApiService.get("vaccination/dashboard");
        this.vaccinationDashboard = response.data.data;
      } catch (e) {
        this.vaccinationDashboard = null;
      }
    },
    newRecord() {
      if (!this.activeDefinition) return;
      const payload = {};
      this.activeDefinition.fields.forEach((field) => {
        payload[field.key] =
          ["array", "resource_quantity"].includes(field.type)
            ? []
            : field.type === "boolean"
            ? false
            : "";
      });
      this.record = {
        id: null,
        patient_id: this.selectedPatient.id,
        appointment_id: null,
        doctor_id: null,
        corporate_mcu_program_id: null,
        corporate_mcu_participant_id: null,
        record_type: this.activeType,
        version: (this.records[0] && Number(this.records[0].version) + 1) || 1,
        status: "draft",
        observed_at: this.localDate(new Date()),
        clinical_payload: payload,
        clinical_summary: "",
        events: [],
      };
      if (this.activeType === "mcu_conclusion") {
        this.mcuSection = "order";
        this.$set(this.record.clinical_payload, "order_reference", `MCU-${new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14)}`);
        this.$set(this.record.clinical_payload, "payer_type", "general");
        this.$set(this.record.clinical_payload, "fitness_result", "pending");
        const appointment = (this.mcuContext.appointments || [])[0];
        if (appointment) {
          this.record.appointment_id = appointment.id;
          this.record.doctor_id = appointment.doctor_id || null;
        }
      }
    },
    async openRecord(id) {
      try {
        const r = await ApiService.get(`vertical-clinical/records/${id}`);
        this.record = r.data.data;
        this.record.observed_at = this.localDate(this.record.observed_at);
        this.record.clinical_payload = this.record.clinical_payload || {};
        this.certificate = null;
        if (
          this.activeType === "vaccination_dose" &&
          this.record.status === "final"
        )
          this.loadCertificate();
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async save(reasonOverride = null) {
      const override = typeof reasonOverride === "string" ? reasonOverride : null;
      const reason = override || window.prompt(
          "Alasan pencatatan/perubahan:",
          this.record.id
            ? "Pembaruan hasil pemeriksaan."
            : "Pencatatan hasil pemeriksaan."
        );
      if (!reason) return;
      try {
        const payload = {
          patient_id: this.selectedPatient.id,
          appointment_id: this.record.appointment_id || null,
          doctor_id: this.record.doctor_id || null,
          corporate_mcu_program_id: this.record.corporate_mcu_program_id || null,
          corporate_mcu_participant_id: this.record.corporate_mcu_participant_id || null,
          observed_at: this.record.observed_at,
          clinical_payload: this.record.clinical_payload,
          clinical_summary: this.record.clinical_summary,
          reason,
        };
        const r = this.record.id
          ? await ApiService.put(
              `vertical-clinical/records/${this.record.id}`,
              payload
            )
          : await ApiService.post(
              `vertical-clinical/records/${this.activeType}`,
              payload
            );
        this.record = r.data.data;
        this.record.observed_at = this.localDate(this.record.observed_at);
        await this.loadRecordList();
        this.toast("Catatan klinis tersimpan.", "success");
        return this.record;
      } catch (e) {
        this.errorMessage = this.error(e);
        throw e;
      }
    },
    async ensureAestheticRecordForMedia() {
      if (!this.record || this.record.id || this.activeType !== "aesthetic_before_after") return;
      try {
        const response = await ApiService.post(
          `vertical-clinical/records/${this.activeType}`,
          {
            patient_id: this.selectedPatient.id,
            observed_at: this.record.observed_at,
            clinical_payload: this.record.clinical_payload,
            clinical_summary: this.record.clinical_summary,
            reason: "Draft dibuat otomatis untuk dokumentasi foto klinis.",
          }
        );
        this.record = response.data.data;
        this.record.observed_at = this.localDate(this.record.observed_at);
        await this.loadRecordList();
        this.toast("Draft dibuat. Foto sedang diunggah.", "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    setAestheticStandardizedView(value) {
      if (!this.record || !this.record.clinical_payload) return;
      this.$set(this.record.clinical_payload, "standardized_view", value);
    },
    async loadRecordList() {
      const r = await ApiService.query("vertical-clinical/records", {
        params: {
          patient_id: this.selectedPatient.id,
          record_type: this.activeType,
          page_size: 100,
        },
      });
      this.records = this.items(r.data);
    },
    async finalize() {
      try {
        const vaccination = this.activeType === "vaccination_dose";
        if (vaccination && !window.confirm("Selesaikan pemberian vaksin? Stok batch akan dikurangi dan catatan akan dikunci.")) return;
        if (!this.record.id && !(await this.save(vaccination ? "Pencatatan pemberian vaksin." : null))) return;
        const reason = vaccination ? "Pemberian vaksin telah diverifikasi dan diselesaikan." : window.prompt(
            "Alasan finalisasi:",
            "Hasil pemeriksaan telah diverifikasi."
          );
        if (!reason) return;
        const r = await ApiService.post(
          `vertical-clinical/records/${this.record.id}/finalize`,
          { reason }
        );
        this.record = r.data.data;
        this.record.observed_at = this.localDate(this.record.observed_at);
        await this.loadRecordList();
        await this.loadVaccinationDashboard();
        this.toast("Catatan final dan terkunci.", "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async revise() {
      const reason = window.prompt("Alasan revisi:");
      if (!reason) return;
      try {
        const r = await ApiService.post(
          `vertical-clinical/records/${this.record.id}/revisions`,
          { reason }
        );
        this.record = r.data.data;
        this.record.observed_at = this.localDate(this.record.observed_at);
        await this.loadRecordList();
        this.toast("Versi revisi dibuat.", "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async voidRecord() {
      const reason = window.prompt("Alasan pembatalan catatan:");
      if (!reason) return;
      try {
        const r = await ApiService.post(
          `vertical-clinical/records/${this.record.id}/void`,
          { reason }
        );
        this.record = r.data.data;
        this.record.observed_at = this.localDate(this.record.observed_at);
        await this.loadRecordList();
        await this.loadVaccinationDashboard();
        this.toast("Catatan dibatalkan.", "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async sendToCashier() {
      try {
        const r = await ApiService.get(
            `vertical-clinical/records/${this.record.id}/billing-preview`
          ),
          preview = r.data.data;
        this.$router.push({
          name: "payment-add",
          query: {
            patient_id: preview.patient.id,
            patient_name: `${preview.patient.name} (${preview.patient.patient_id})`,
            vertical_record_id: preview.record_id,
          },
        });
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    printMcuReport() {
      if (!this.record || this.activeType !== "mcu_conclusion") return;
      const p = this.record.clinical_payload || {}, patient = this.selectedPatient || {};
      const esc = value => String(value === null || value === undefined ? "-" : value).replace(/[&<>'"]/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[char]));
      const list = value => Array.isArray(value) && value.length ? value.join(", ") : (value || "-");
      const fitness = ({ fit: "FIT", fit_with_restriction: "FIT DENGAN PEMBATASAN", temporarily_unfit: "UNFIT SEMENTARA", unfit: "UNFIT", pending: "PENDING" })[p.fitness_result] || p.fitness_result;
      const rows = [
        ["Berat / Tinggi / IMT", `${p.weight_kg || "-"} kg / ${p.height_cm || "-"} cm / ${p.bmi || "-"}`],
        ["Tekanan darah", p.blood_pressure], ["Nadi / Napas / Suhu", `${p.pulse_bpm || "-"} bpm / ${p.respiratory_rate || "-"} x/menit / ${p.temperature_c || "-"} °C`],
        ["Riwayat kesehatan", list(p.health_history)], ["Riwayat keluarga", list(p.family_health_history)], ["Riwayat kerja", list(p.work_history)], ["Kebiasaan", list(p.habits)],
        ["Laboratorium", p.lab_summary], ["Radiologi", p.radiology_summary], ["Spirometri", p.spirometry_summary], ["EKG", p.ekg_summary], ["Audiometri", p.audiometry_summary],
      ].map(row => `<tr><th>${esc(row[0])}</th><td>${esc(row[1])}</td></tr>`).join("");
      const html = `<!doctype html><html><head><title>${esc(this.record.record_number)}</title><style>body{font:13px Arial;color:#172033;margin:36px}h1{margin:0;color:#0f766e}h2{font-size:16px;margin-top:26px;border-bottom:1px solid #bbb;padding-bottom:6px}.meta{display:grid;grid-template-columns:1fr 1fr;gap:6px 30px;margin:18px 0}table{width:100%;border-collapse:collapse}th,td{padding:7px;border:1px solid #ccd5df;text-align:left;vertical-align:top}th{width:30%;background:#f4f8f8}.result{padding:14px;border:2px solid #0f766e;text-align:center;font-size:18px;font-weight:bold}.sign{margin-top:60px;margin-left:auto;width:240px;text-align:center}@media print{button{display:none}}</style></head><body><h1>HASIL MEDICAL CHECK UP</h1><p>${esc(this.record.record_number)} · ${esc(this.dateOnly(this.record.observed_at))}</p><div class="meta"><span>Pasien: <b>${esc(patient.name)}</b></span><span>No. RM: <b>${esc(this.patientNumber(patient))}</b></span><span>Perusahaan: <b>${esc(p.company_name || "-")}</b></span><span>No. MCU: <b>${esc(p.order_reference)}</b></span></div><h2>Pemeriksaan</h2><table>${rows}</table><h2>Temuan & Kesimpulan</h2><p>${esc(p.findings)}</p><div class="result">${esc(fitness)}</div><p><b>Pembatasan:</b> ${esc(list(p.restrictions))}</p><p><b>Rekomendasi:</b> ${esc(p.recommendation)}</p><div class="sign">Dokter Pemeriksa<br><br><br><b>${esc((this.mcuContext.doctors || []).find(d => Number(d.id) === Number(this.record.doctor_id))?.name || "")}</b></div><script>window.onload=()=>window.print()<\/script></body></html>`;
      const printWindow = window.open("", "_blank");
      if (printWindow) { printWindow.document.open(); printWindow.document.write(html); printWindow.document.close(); }
    },
    openInvoice() {
      this.$router.push({
        name: "payment-detail",
        params: { id: this.record.billing.id },
      });
    },
    async loadCertificate() {
      try {
        const r = await ApiService.get(
          `vaccination/records/${this.record.id}/certificate`
        );
        this.certificate = r.data.data;
      } catch (e) {
        if (!e.response || e.response.status !== 404)
          this.errorMessage = this.error(e);
      }
    },
    async issueCertificate() {
      if (this.certificate) {
        document
          .querySelector(".certificate-print")
          .scrollIntoView({ behavior: "smooth" });
        return;
      }
      const reason = window.prompt(
        "Alasan penerbitan sertifikat:",
        "Pemberian vaksin telah diverifikasi."
      );
      if (!reason) return;
      try {
        const r = await ApiService.post(
          `vaccination/records/${this.record.id}/certificate`,
          { reason }
        );
        this.certificate = r.data.data;
        this.toast("Sertifikat vaksinasi diterbitkan.", "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async revokeCertificate() {
      const reason = window.prompt("Alasan pencabutan sertifikat:");
      if (!reason) return;
      try {
        const r = await ApiService.post(
          `vaccination/records/${this.record.id}/certificate/revoke`,
          { reason }
        );
        this.certificate = r.data.data;
        this.toast("Sertifikat dicabut.", "success");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async copyVerificationUrl() {
      try {
        await navigator.clipboard.writeText(this.verificationUrl);
        this.toast("Tautan verifikasi disalin.", "success");
      } catch (e) {
        this.errorMessage = "Browser tidak mengizinkan penyalinan otomatis.";
      }
    },
    windowPrint() {
      window.print();
    },
    payloadOf(row) {
      if (!row || !row.clinical_payload) return {};
      if (typeof row.clinical_payload === "object") return row.clinical_payload;
      try {
        return JSON.parse(row.clinical_payload) || {};
      } catch (e) {
        return {};
      }
    },
    arrayValue(key) {
      const value = this.record.clinical_payload[key];
      return Array.isArray(value) ? value.join("\n") : value || "";
    },
    setArrayValue(key, value) {
      this.$set(
        this.record.clinical_payload,
        key,
        value
          .split("\n")
          .map((x) => x.trim())
          .filter(Boolean)
      );
    },
    addClinicalResourceLine(key) {
      if (!Array.isArray(this.record.clinical_payload[key])) this.$set(this.record.clinical_payload, key, []);
      this.record.clinical_payload[key].push({ resource_id: "", quantity: 1, unit: "" });
    },
    removeClinicalResourceLine(key, index) {
      this.record.clinical_payload[key].splice(index, 1);
    },
    applyResourceDefaults(field) {
      const payload = this.record.clinical_payload;
      const selected = (this.resourceOptions[field.resource_type] || []).find((item) => Number(item.id) === Number(payload[field.key]));
      if (!selected) return;
      if (field.resource_type === "aesthetic_treatment_package") {
        const first = (selected.operational_payload.treatment_items || [])[0];
        if (first && !payload.treatment_resource_id) {
          this.$set(payload, "treatment_resource_id", first.resource_id);
          this.applyResourceDefaults({ key: "treatment_resource_id", resource_type: "aesthetic_treatment" });
        }
      }
      if (field.resource_type === "aesthetic_treatment") {
        this.$set(payload, "treatment_name", selected.name);
        if (!payload.consent_reference && selected.operational_payload.consent_template) this.$set(payload, "consent_reference", selected.operational_payload.consent_template);
        const batches = this.resourceOptions.aesthetic_product_batch || [];
        const lines = (selected.operational_payload.product_requirements || []).map((requirement) => {
          const candidates = batches.filter((batch) => Number(batch.operational_payload.product_resource_id) === Number(requirement.resource_id) && Number(batch.operational_payload.quantity || 0) > 0).sort((a, b) => String(a.operational_payload.expiry_date).localeCompare(String(b.operational_payload.expiry_date)));
          return { resource_id: candidates[0] ? candidates[0].id : "", quantity: requirement.quantity, unit: requirement.unit || "" };
        });
        this.$set(payload, "batch_consumptions", lines);
      }
      if (field.resource_type === "vaccination_vaccine_batch") {
        const batch = selected.operational_payload || {};
        this.$set(payload, "batch_number", batch.batch_number || selected.resource_code);
        this.$set(payload, "expiry_date", batch.expiry_date || "");
        this.$set(payload, "cold_chain_verified", (batch.cold_chain_status || "released") === "released");
        if (!payload.dose_quantity) this.$set(payload, "dose_quantity", 1);
        if (!payload.dose_unit) this.$set(payload, "dose_unit", "dose");
      }
    },
    resourceOptionLabel(option, type) {
      const payload = option.operational_payload || {};
      if (type === "aesthetic_product_batch") return `${option.name} · ${payload.batch_number || option.resource_code} · stok ${Number(payload.quantity || 0).toLocaleString("id-ID")} · exp ${payload.expiry_date || "-"}`;
      if (type === "vaccination_vaccine_batch") return `${option.name} · batch ${payload.batch_number || option.resource_code} · stok ${Number(payload.quantity || 0).toLocaleString("id-ID")} · exp ${payload.expiry_date || "-"} · ${payload.cold_chain_status === "released" ? "layak" : "karantina"}`;
      if (["eye_frame", "eye_lens"].includes(type)) {
        const inventory = payload.inventory_snapshot || {};
        return `${option.name} · ${inventory.code || option.resource_code} · ${inventory.category || "Alkes"} · stok ${Number(payload.stock_quantity || 0).toLocaleString("id-ID")}`;
      }
      const price = payload.price !== undefined ? payload.price : payload.standalone_price;
      return `${option.name} · ${option.resource_code}${price !== undefined ? ` · ${this.money(price)}` : ""}`;
    },
    applyClinicalDefaults(key) {
      if (!this.record || !this.record.clinical_payload) return;
      const payload = this.record.clinical_payload;
      if (key === "gestational_age_weeks" && payload.gestational_age_weeks !== "") {
        const weeks = Number(payload.gestational_age_weeks);
        this.$set(payload, "trimester", weeks < 14 ? "1" : weeks < 28 ? "2" : "3");
      }
      if (key === "age_months" && payload.age_months !== "") {
        const months = Number(payload.age_months);
        this.$set(payload, "measurement_position", months <= 24 ? "recumbent" : "standing");
        this.$set(payload, "reference", months <= 60 ? "who_2006" : "who_2007");
      }
      if (["weight_kg", "height_cm"].includes(key) && Number(payload.weight_kg) > 0 && Number(payload.height_cm) > 0) {
        const meters = Number(payload.height_cm) / 100;
        this.$set(payload, "bmi", Number((Number(payload.weight_kg) / (meters * meters)).toFixed(2)));
      }
    },
    booleanLabel(key) {
      if (key === "referral_required") return "Ya, pasien memerlukan rujukan";
      if (key === "contraindication_cleared") return "Skrining aman, tidak ada kontraindikasi";
      if (key === "adverse_event") return "Ya, terdapat KIPI / reaksi merugikan";
      return "Terverifikasi";
    },
    inputType(type) {
      return type === "date"
        ? "date"
        : type === "number" || type === "integer"
        ? "number"
        : "text";
    },
    patientNumber(patient) {
      return (
        patient.local_medical_record_number ||
        patient.patient_id ||
        patient.patient_number ||
        patient.medical_record_number ||
        `ID ${patient.id}`
      );
    },
    resourceIcon(type) {
      return type.includes("batch")
        ? "fas fa-boxes"
        : type.includes("package")
        ? "fas fa-layer-group"
        : type === "aesthetic_treatment"
        ? "fas fa-magic"
        : type === "aesthetic_product"
        ? "fas fa-prescription-bottle-alt"
        : type === "eye_diagnostic_test"
        ? "fas fa-microscope"
        : type === "eye_frame"
        ? "fas fa-glasses"
        : type === "eye_lens"
        ? "fas fa-circle-notch"
        : type.includes("cold_chain")
        ? "fas fa-temperature-low"
        : type.includes("program")
        ? "fas fa-project-diagram"
        : "fas fa-box-open";
    },
    verticalLabel(code) {
      return (
        {
          dental: "Klinik Gigi",
          aesthetic: "Klinik Kecantikan",
          maternal_child: "Ibu & Anak",
          mcu: "Medical Check Up",
          eye: "Klinik Mata",
          physiotherapy: "Fisioterapi",
          occupational: "Kesehatan Kerja",
          vaccination: "Vaksinasi",
        }[code] || code
      );
    },
    money(value) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    statusLabel(status) {
      return (
        { draft: "Draft", final: "Final", superseded: "Digantikan revisi", void: "Dibatalkan" }[status] || status
      );
    },
    localDate(value) {
      if (!value) return null;
      const d = new Date(value);
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
    },
    dateTime(value) {
      return value
        ? new Intl.DateTimeFormat("id-ID", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(value))
        : "-";
    },
    dateOnly(value) {
      return value ? new Intl.DateTimeFormat("id-ID", { dateStyle: "medium" }).format(new Date(value)) : "-";
    },
    toast(message, variant) {
      this.$bvToast.toast(message, {
        title: variant === "success" ? "Berhasil" : "Perlu diperiksa",
        variant,
        solid: true,
      });
    },
    error(e) {
      const d = e && e.response && e.response.data;
      if (d && d.errors) return Object.values(d.errors).flat()[0];
      return (
        (d && (d.message || d.error)) || "Permintaan belum dapat diproses."
      );
    },
  },
};
</script>

<style scoped>
.vaccination-dashboard{display:grid;grid-template-columns:repeat(4,minmax(0,1fr)) auto;gap:10px;align-items:center;margin:12px 0;padding:12px}.vaccination-metric{min-height:72px;padding:11px 12px;background:#f7fafc;border:1px solid #e5edf4;border-radius:10px}.vaccination-metric small,.vaccination-metric strong,.vaccination-metric span{display:block}.vaccination-metric small{color:#718096;font-size:9px;font-weight:800;letter-spacing:.06em}.vaccination-metric strong{margin:3px 0;color:#17324d;font-size:18px}.vaccination-metric span{color:#718096;font-size:9px}.vaccination-metric.attention{background:#fff7ed;border-color:#fdba74}.vaccination-metric.attention strong{color:#c2410c}@media(max-width:1100px){.vaccination-dashboard{grid-template-columns:repeat(2,minmax(0,1fr))}.vaccination-dashboard>button{grid-column:1/-1}}
.vertical-workspace {
  padding-bottom: 2rem;
  color: #183153;
}
.vertical-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.7rem 2rem;
  border-radius: 22px;
  background: linear-gradient(130deg, #4338ca, #0f766e);
  color: #fff;
  box-shadow: 0 18px 42px rgba(67, 56, 202, 0.16);
}
.vertical-hero h1 {
  font-size: 1.7rem;
  margin: 0.3rem 0;
}
.vertical-hero p {
  margin: 0;
  color: #dbeafe;
}
.eyebrow {
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  font-weight: 800;
}
.patient-picker {
  position: relative;
  width: min(420px, 100%);
}
.patient-picker label {
  font-size: 0.72rem;
  font-weight: 700;
}
.patient-picker > div:nth-child(2) {
  display: flex;
  align-items: center;
  margin-top: 0.35rem;
  padding: 0 0.8rem;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
}
.patient-picker input {
  flex: 1;
  border: 0;
  outline: 0;
  padding: 0.8rem;
}
.patient-results {
  position: absolute;
  top: 68px;
  z-index: 20;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}
.patient-results button {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.7rem 1rem;
  border: 0;
  border-bottom: 1px solid #edf2f4;
  background: #fff;
  text-align: left;
}
.patient-results small {
  color: #64748b;
}
.type-strip {
  display: flex;
  gap: 0.6rem;
  margin: 1rem 0;
  overflow: auto;
}
.type-strip button {
  display: flex;
  flex-direction: column;
  min-width: 180px;
  padding: 0.75rem 1rem;
  border: 1px solid #dbe7ef;
  border-radius: 12px;
  background: #fff;
  text-align: left;
}
.type-strip button.active {
  border-color: #14b8a6;
  background: #f0fdfa;
}
.type-strip span {
  font-size: 0.62rem;
  text-transform: uppercase;
  color: #64748b;
}
.type-strip strong {
  font-size: 0.78rem;
  margin-top: 0.2rem;
}
.empty-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 5rem;
  text-align: center;
}
.empty-state i {
  display: grid;
  place-items: center;
  width: 76px;
  height: 76px;
  border-radius: 22px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 2rem;
}
.empty-state h3 {
  margin: 1rem 0 0.3rem;
}
.empty-state p {
  color: #64748b;
}
.workspace-grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1rem;
}
.record-history,
.record-card {
  border: 1px solid #e3ebf1;
  border-radius: 16px;
  background: #fff;
}
.record-history {
  align-self: start;
  padding: 0.8rem;
}
.aside-heading,
.record-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.aside-heading {
  padding: 0.35rem;
}
.aside-heading small,
.aside-heading strong {
  display: block;
}
.aside-heading small {
  color: #64748b;
}
.aside-heading button {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 9px;
  background: #e6fffa;
  color: #0f766e;
}
.history-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f8fafc;
  text-align: left;
}
.history-item.active {
  border-color: #14b8a6;
  background: #f0fdfa;
}
.history-item span {
  font-size: 0.65rem;
  color: #64748b;
}
.history-item strong {
  font-size: 0.75rem;
  margin: 0.2rem 0;
}
.history-item small {
  font-weight: 700;
}
.history-empty {
  padding: 1rem;
  text-align: center;
  color: #94a3b8;
}
.record-card {
  padding: 1.25rem;
}
.record-heading {
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #edf2f4;
}
.record-heading span {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #0f766e;
}
.record-heading h2 {
  margin: 0.2rem 0;
}
.record-heading p {
  margin: 0;
  color: #64748b;
}
.heading-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.status-pill {
  padding: 0.4rem 0.7rem;
  border-radius: 99px;
  background: #edf2f4;
  font-weight: 800;
}
.status-final {
  color: #087f5b;
}
.status-draft {
  color: #b45309;
}
.status-void {
  color: #dc2626;
}
.status-superseded {
  color: #64748b;
}
.clinical-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1.25rem 0;
}
.form-field.wide {
  grid-column: span 2;
}
.form-field label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
}
.form-field label b {
  color: #dc2626;
}
.boolean-input {
  display: flex !important;
  align-items: center;
  gap: 0.6rem;
  min-height: 38px;
}
.boolean-input input {
  width: 18px;
  height: 18px;
}
.audit-trail {
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
}
.audit-trail summary {
  cursor: pointer;
  font-weight: 700;
}
.audit-trail > div {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: 0.7rem;
  padding: 0.65rem 0;
  border-top: 1px solid #edf2f4;
}
.audit-trail span,
.audit-trail small {
  color: #64748b;
}
@media (max-width: 850px) {
  .vertical-hero {
    align-items: flex-start;
    flex-direction: column;
  }
  .workspace-grid {
    grid-template-columns: 1fr;
  }
  .record-history {
    display: flex;
    gap: 0.5rem;
    overflow: auto;
  }
  .history-item {
    min-width: 150px;
  }
  .clinical-form {
    grid-template-columns: 1fr;
  }
  .form-field.wide {
    grid-column: auto;
  }
  .record-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media (max-width: 520px) {
  .vertical-hero {
    padding: 1.25rem;
  }
  .audit-trail > div {
    grid-template-columns: 1fr;
  }
}
.certificate-print {
  margin: 1rem 0;
  padding: 2rem;
  border: 2px solid #0f766e;
  border-radius: 16px;
  background: linear-gradient(145deg, #fff, #f0fdfa);
  text-align: center;
}
.certificate-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #0f766e;
  font-weight: 800;
  letter-spacing: 0.12em;
}
.certificate-print h2 {
  margin: 1rem 0 0.2rem;
  font-family: Georgia, serif;
  font-size: 2rem;
}
.certificate-number {
  color: #64748b;
}
.certificate-data {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
  text-align: left;
}
.certificate-data > div {
  padding: 0.8rem;
  border-bottom: 1px solid #cbd5e1;
}
.certificate-data span {
  display: block;
  font-size: 0.68rem;
  color: #64748b;
}
.verification-code {
  word-break: break-all;
  font-family: monospace;
  font-size: 0.7rem;
  color: #475569;
}
.certificate-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}
@media print {
  body * {
    visibility: hidden !important;
  }
  .certificate-print,
  .certificate-print * {
    visibility: visible !important;
  }
  .certificate-print {
    position: absolute;
    inset: 0;
    margin: 0;
    border: 0;
  }
  .certificate-actions {
    display: none !important;
  }
}
@media (max-width: 600px) {
  .certificate-data {
    grid-template-columns: 1fr;
  }
}
</style>
<style scoped>
.workspace-switch {
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
}
.workspace-switch button {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  color: #40566a;
  font-weight: 700;
}
.workspace-switch button.active {
  border-color: #0f766e;
  background: #ecfdf5;
  color: #0f766e;
}
.workspace-switch button:focus-visible {
  outline: 3px solid rgba(15, 118, 110, 0.24);
  outline-offset: 2px;
}
.vertical-hero {
  background: linear-gradient(130deg, #3730a3, #0f766e);
}
.vertical-hero p {
  color: #eff6ff;
}
.vertical-workspace .form-control:focus {
  border-color: #0f766e;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.14);
}
.vertical-workspace .form-control:disabled {
  color: #40566a;
  opacity: 1;
}
.history-item small,
.record-heading p {
  color: #52677a;
}
.type-strip button:focus-visible,
.history-item:focus-visible,
.patient-results button:focus-visible {
  outline: 3px solid rgba(15, 118, 110, 0.24);
  outline-offset: 2px;
}
@media (max-width: 520px) {
  .workspace-switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .workspace-switch button {
    justify-content: center;
  }
}
</style>
<style scoped>
.progress-chart {
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid #dbe7ef;
  border-radius: 14px;
  background: #f8fafc;
}
.progress-chart > div:first-child {
  display: flex;
  justify-content: space-between;
}
.progress-chart span {
  font-size: 0.7rem;
  color: #64748b;
}
.progress-chart svg {
  width: 100%;
  height: 180px;
}
.progress-chart line {
  stroke: #cbd5e1;
  stroke-width: 1;
}
.progress-chart polyline {
  fill: none;
  stroke: #0f766e;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.progress-chart circle {
  fill: #fff;
  stroke: #0f766e;
  stroke-width: 3;
}
.trend-legend {
  display: flex;
  gap: 0.5rem;
  overflow: auto;
}
.trend-legend span {
  min-width: 90px;
  padding: 0.45rem;
  border-radius: 8px;
  background: #fff;
}
.trend-legend b {
  display: block;
  color: #183153;
}
.patient-start {
  padding: 1.2rem;
}
.clinical-tool-switcher {
  display: grid;
  grid-template-columns: minmax(230px, 0.36fr) minmax(420px, 1fr);
  align-items: center;
  gap: 1rem;
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #dce5ef;
  border-left: 4px solid #11958f;
  border-radius: 14px;
  background: linear-gradient(110deg, #f0fdfa 0%, #ffffff 48%);
}
.clinical-tool-switcher__intro {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}
.clinical-tool-switcher__intro > span {
  display: grid;
  place-items: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: #ccfbf1;
  color: #0f766e;
}
.clinical-tool-switcher__intro small,
.clinical-tool-switcher__intro strong,
.clinical-tool-switcher__intro p {
  display: block;
}
.clinical-tool-switcher__intro small {
  color: #0f766e;
  font-size: 0.61rem;
  font-weight: 800;
  letter-spacing: 0.09em;
}
.clinical-tool-switcher__intro strong {
  margin: 0.1rem 0;
  color: #183153;
  font-size: 0.82rem;
}
.clinical-tool-switcher__intro p {
  margin: 0;
  color: #64748b;
  font-size: 0.68rem;
}
.clinical-tool-switcher ::v-deep .workspace-tabs {
  margin: 0;
  border-color: #cfe5e2;
  box-shadow: none;
}
.clinical-tool-switcher ::v-deep .workspace-tabs button {
  min-width: 150px;
}
.patient-start-intro {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 43%);
  align-items: center;
  gap: 2rem;
  padding: 1.2rem;
  border-radius: 14px;
  background: #f8fafc;
}
.patient-start-intro .eyebrow {
  color: #0f766e;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
}
.patient-start-intro h2 {
  margin: 0.3rem 0;
}
.worklist-heading > div {
  display: flex;
  flex-direction: column;
}
.worklist-heading small {
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0;
}
.worklist-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.25rem 0 0.65rem;
}
.worklist-heading > span {
  color: #64748b;
  font-size: 0.7rem;
  white-space: nowrap;
}
.patient-worklist {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.worklist-patient {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) minmax(110px, 145px) auto;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  min-width: 0;
  padding: 0.9rem;
  border: 1px solid #dbe7ef;
  border-radius: 13px;
  background: #fff;
  color: #183153;
  text-align: left;
}
.worklist-patient:hover {
  border-color: #14b8a6;
  background: #f0fdfa;
}
.worklist-avatar {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #ccfbf1;
  color: #0f766e;
  font-weight: 800;
}
.worklist-main,
.worklist-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.worklist-main small,
.worklist-meta small {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0;
}
.worklist-meta {
  align-items: flex-end;
  text-align: right;
}
.worklist-meta strong {
  font-size: 0.72rem;
  overflow-wrap: anywhere;
}
.worklist-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
}
.worklist-badges b {
  padding: 0.15rem 0.4rem;
  border-radius: 99px;
  background: #e0f2fe;
  color: #075985;
  font-size: 0.62rem;
}
.worklist-badges b.risk {
  background: #fff7ed;
  color: #c2410c;
}
.worklist-loading,
.worklist-empty {
  padding: 1.5rem;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #64748b;
  text-align: center;
}
.first-record-empty {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 4rem 1rem;
  text-align: center;
}
.first-record-empty > span {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: #e6fffa;
  color: #0f766e;
  font-size: 1.5rem;
}
.first-record-empty h2 {
  margin: 1rem 0 0.35rem;
}
.first-record-empty p {
  max-width: 520px;
  color: #64748b;
}
.aside-heading .new-record-button {
  display: inline-flex;
  width: auto;
  padding: 0 0.65rem;
  white-space: nowrap;
}
@media (max-width: 850px) {
  .clinical-tool-switcher {
    grid-template-columns: 1fr;
  }
  .patient-worklist,
  .patient-start-intro {
    grid-template-columns: 1fr;
  }
  .worklist-meta {
    display: none;
  }
}
.patient-start p {
  max-width: 620px;
  margin: 0;
  color: #52677a;
}
@media (max-width: 820px) {
  .patient-start .patient-picker-standard {
    width: 100%;
  }
}
@media (max-width: 520px) {
  .clinical-tool-switcher {
    padding: 0.75rem;
  }
  .clinical-tool-switcher__intro p {
    display: none;
  }
  .patient-start {
    padding: 0.75rem;
  }
}
.selected-patient-banner {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.8rem;
  margin: 1rem 0;
  padding: 0.8rem 1rem;
  border-color: #bfe9df;
  background: #f0fdfa;
}
.selected-patient-banner__avatar {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #ccfbf1;
  color: #0f766e;
  font-weight: 800;
}
.selected-patient-banner > div > * {
  display: block;
}
.selected-patient-banner small {
  color: #52677a;
  font-size: 0.66rem;
}
.selected-patient-banner strong {
  margin: 0.18rem 0;
  color: #134e4a;
}
.selected-patient-banner > div > span {
  font-size: 0.68rem;
  color: #0f766e;
}
.selected-patient-banner button {
  padding: 0.38rem 0.6rem;
  border: 1px solid #99f6e4;
  border-radius: 8px;
  background: #fff;
  color: #0f766e;
  font-size: 0.7rem;
  font-weight: 800;
}
.patient-packages {
  display: flex;
  align-items: stretch;
  gap: 0.7rem;
  margin: 0 0 1rem;
  padding: 0.8rem 1rem;
  overflow-x: auto;
}
.patient-packages > div,
.patient-packages article { display: flex; flex-direction: column; min-width: 190px; }
.patient-packages > div { justify-content: center; }
.patient-packages article { padding: 0.65rem 0.8rem; border: 1px solid #ccfbf1; border-radius: 10px; background: #f0fdfa; }
.patient-packages small { color: #64748b; }
.eye-care-path {
  display: grid;
  grid-template-columns: minmax(220px, 0.75fr) minmax(420px, 1.6fr) auto;
  align-items: center;
  gap: 1rem;
  margin: 0 0 1rem;
  padding: 1rem;
  border-color: #c7dffc;
  background: linear-gradient(135deg, #f8fbff 0%, #fff 70%);
}
.eye-care-path__intro { display: flex; align-items: flex-start; gap: 0.75rem; }
.eye-care-path__intro > span {
  display: grid; place-items: center; flex: 0 0 42px; height: 42px;
  border-radius: 12px; background: #dbeafe; color: #1d4ed8; font-size: 1rem;
}
.eye-care-path__intro small { display: block; color: #2563eb; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.08em; }
.eye-care-path__intro strong { display: block; margin: 0.2rem 0; color: #183153; }
.eye-care-path__intro p { margin: 0; color: #64748b; font-size: 0.68rem; line-height: 1.45; }
.eye-care-steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.5rem; }
.eye-care-steps button {
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.5rem;
  min-width: 0; padding: 0.65rem; border: 1px solid #dbe7ef; border-radius: 11px;
  background: #fff; color: #334155; text-align: left;
}
.eye-care-steps button:hover, .eye-care-steps button.active { border-color: #60a5fa; background: #eff6ff; }
.eye-care-steps button > span { display: grid; place-items: center; width: 26px; height: 26px; border-radius: 99px; background: #e2e8f0; color: #475569; font-size: 0.68rem; font-weight: 800; }
.eye-care-steps button.complete > span { background: #dcfce7; color: #15803d; }
.eye-care-steps button > div { min-width: 0; }
.eye-care-steps strong, .eye-care-steps small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.eye-care-steps strong { font-size: 0.72rem; }
.eye-care-steps small { color: #64748b; font-size: 0.61rem; }
.eye-care-steps button > i { color: #94a3b8; font-size: 0.62rem; }
.eye-care-links { display: flex; flex-direction: column; gap: 0.4rem; }
.eye-care-links button { padding: 0.45rem 0.6rem; border: 1px solid #dbeafe; border-radius: 8px; background: #fff; color: #1d4ed8; font-size: 0.68rem; font-weight: 800; white-space: nowrap; }
.eye-care-links i { width: 16px; margin-right: 0.25rem; }
.clinical-form .eye-field { padding: 0.75rem; border-radius: 11px; }
.clinical-form .eye-field--od { border-left: 3px solid #3b82f6; background: #f8fbff; }
.clinical-form .eye-field--os { border-left: 3px solid #14b8a6; background: #f7fffd; }
.eye-fhir-warnings { padding-left: 1.2rem; color: #92400e; font-size: 0.78rem; }
.eye-fhir-preview { max-height: 58vh; padding: 1rem; overflow: auto; border-radius: 12px; background: #0f172a; color: #dbeafe; font-size: 0.7rem; white-space: pre-wrap; }
.eye-optical-tracker { display: grid; grid-template-columns: minmax(240px, 1fr) auto; gap: 1rem; margin: 0 1rem 1rem; padding: 1rem; border: 1px solid #bfdbfe; border-radius: 14px; background: #f8fbff; }
.eye-optical-tracker__summary { display: flex; align-items: center; gap: 0.75rem; }
.eye-optical-tracker__summary > span { display: grid; place-items: center; width: 42px; height: 42px; border-radius: 12px; background: #dbeafe; color: #1d4ed8; }
.eye-optical-tracker__summary small, .eye-optical-tracker__summary strong, .eye-optical-tracker__summary p { display: block; margin: 0; }
.eye-optical-tracker__summary small { color: #2563eb; font-size: 0.62rem; font-weight: 800; }
.eye-optical-tracker__summary strong { margin: 0.18rem 0; color: #183153; }
.eye-optical-tracker__summary p { color: #64748b; font-size: 0.68rem; }
.eye-optical-tracker__actions { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; }
.eye-optical-tracker details { grid-column: 1 / -1; padding-top: 0.65rem; border-top: 1px solid #dbeafe; }
.eye-optical-tracker details div { display: grid; grid-template-columns: 180px 1fr; gap: 0.75rem; padding: 0.35rem 0; font-size: 0.68rem; color: #64748b; }
.eye-optical-tracker details strong { color: #334155; }
.clinical-resource-lines {
  display: grid;
  gap: 0.65rem;
  padding: 0.8rem;
  border: 1px solid #dbe7ef;
  border-radius: 12px;
  background: #f8fafc;
}
.clinical-resource-line {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 120px 90px auto;
  gap: 0.55rem;
  align-items: center;
}
.clinical-resource-lines > small { color: #64748b; }
.mcu-stepper {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.45rem;
  padding: 0.75rem;
  border: 1px solid #cde9e6;
  border-radius: 13px;
  background: #f7fcfb;
}
.mcu-stepper button {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  padding: 0.55rem;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: #64748b;
  text-align: left;
}
.mcu-stepper button.active { border-color: #70c7c0; background: #fff; color: #0f766e; box-shadow: 0 3px 10px rgba(15, 118, 110, 0.08); }
.mcu-stepper span { display: grid; place-items: center; flex: 0 0 24px; height: 24px; border-radius: 99px; background: #d9efed; font-size: 0.66rem; font-weight: 800; }
.mcu-stepper strong { overflow: hidden; font-size: 0.68rem; text-overflow: ellipsis; white-space: nowrap; }
.mcu-order-context { display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem; padding: 0.9rem; border: 1px solid #dbe7ef; border-radius: 12px; background: #fff; }
@media (max-width: 900px) {
  .eye-care-path { grid-template-columns: 1fr; }
  .eye-optical-tracker { grid-template-columns: 1fr; }
  .eye-care-links { flex-direction: row; }
  .clinical-resource-line { grid-template-columns: 1fr 1fr; }
  .mcu-stepper { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 520px) {
  .eye-care-steps { grid-template-columns: 1fr; }
  .eye-care-links { align-items: stretch; flex-direction: column; }
  .selected-patient-banner {
    grid-template-columns: auto minmax(0, 1fr);
  }
  .selected-patient-banner button {
    grid-column: span 2;
  }
  .mcu-stepper, .mcu-order-context { grid-template-columns: 1fr; }
}
</style>
